import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import apiClient from '../../api/client';
import ImageWithFallback from '../../components/ImageWithFallback';
import SectionHeader from '../../components/ui/section-header';
import { ICommonResponse } from '../../core/api/http-methods/common-response.type';

type EntitySummary = {
  entity_id: string;
  entity_name: string;
  entity_logo_image: string;
  is_new?: boolean;
};

type GetRewardBrandsResponse = ICommonResponse<{
  participating: EntitySummary[];
  myJoined: EntitySummary[];
}>;

export default function MyRewardsHub() {
  const { data: participatingRewardsData } = useQuery<GetRewardBrandsResponse>({
    queryKey: ['getParticipatingBrands'],
    queryFn: async () => {
      const resp = await apiClient.get('rewards/brands');
      return resp.data || {};
    }
  });

  let rewards: EntitySummary[] = [];
  let participatingRewards: EntitySummary[] = [];
  if (participatingRewardsData?.success) {
    participatingRewards = participatingRewardsData?.data?.participating || [];
    rewards = participatingRewardsData?.data?.myJoined || [];
  }

  return (
    <section className="flex min-h-[calc(100dvh-9rem)] flex-col gap-y-4">
      <SectionHeader
        href={'/'}
        title={
          <>
            <h1 className="text-md font-bold leading-6 text-gray-900 md:text-lg">
              My Rewards Hub
            </h1>
          </>
        }
      />

      {rewards.length > 0 && (
        <div className="flex flex-1 flex-col gap-y-2">
          <h3 className="px-6 text-lg font-normal">My Joined Rewards</h3>
          <div className="grid grid-cols-4 gap-4 px-6 sm:grid-cols-5 md:grid-cols-6 xl:grid-cols-7">
            {rewards.map((entity) => (
              <Link
                key={entity.entity_id}
                to={`/my-rewards-hub/${entity.entity_id}`}
                className="flex flex-col items-center justify-start gap-y-2 rounded-lg p-2 transition-colors hover:bg-gray-50"
              >
                <div className="relative size-16 rounded-2xl bg-white p-2 shadow-lg">
                  <ImageWithFallback
                    src={entity?.entity_logo_image || ''}
                    alt={entity?.entity_name || ''}
                    className="size-full object-contain"
                  />
                  {entity.is_new && (
                    <div className="absolute -right-1 -top-1 rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                      NEW
                    </div>
                  )}
                </div>
                <p className="line-clamp-2 w-full break-words text-center text-xs font-normal leading-tight">
                  {entity.entity_name}
                </p>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-y-2 px-6">
        {participatingRewards.length > 0 && (
          <h3 className="ml-2 mt-3 text-lg font-normal">
            Participating Brands
          </h3>
        )}
        <div className="grid grid-cols-4 gap-4 sm:grid-cols-5 md:grid-cols-6 xl:grid-cols-7">
          {participatingRewards.map((entity) => (
            <Link
              key={entity.entity_id}
              to={`/my-rewards-hub/${entity.entity_id}`}
              className="flex flex-col items-center justify-start gap-y-2 rounded-lg p-2 transition-colors hover:bg-gray-50"
            >
              <div className="relative size-16 rounded-2xl bg-white p-2 shadow-lg">
                <ImageWithFallback
                  src={entity?.entity_logo_image || ''}
                  alt={entity?.entity_name || ''}
                  className="size-full rounded-2xl object-contain"
                />
                {entity.is_new && (
                  <div className="absolute -right-1 -top-1 rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                    NEW
                  </div>
                )}
              </div>
              <p className="line-clamp-2 w-full break-words text-center text-xs font-normal leading-tight">
                {entity.entity_name}
              </p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
